import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Building2, Search, Eye, Pencil, Ban, CheckCircle,
  RefreshCw, Trash2, X, ChevronDown, CreditCard, LogIn, DollarSign, Pause, Play,
} from 'lucide-react';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_CONFIG = {
  active: { className: 'sa-badge-active', label: 'Active' },
  trialing: { className: 'sa-badge-trialing', label: 'Trial' },
  pending: { className: 'sa-badge-pending', label: 'Pending' },
  past_due: { className: 'sa-badge-pending', label: 'Past Due' },
  expired: { className: 'sa-badge-expired', label: 'Expired' },
  cancelled: { className: 'sa-badge-cancelled', label: 'Cancelled' },
  suspended: { className: 'sa-badge-suspended', label: 'Suspended' },
};

/* ============================================================
   ASSIGN / CHANGE PLAN MODAL
   ============================================================ */
function AssignModal({ company, plans, onClose, onSave }) {
  const [form, setForm] = useState({
    company_id: company.id,
    plan_id: plans.find(p => p.name === company.plan_name)?.id || plans[0]?.id || '',
    billing_cycle: company.billing_cycle || 'monthly',
    trial_days: 14,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await subscriptionsAPI.assign(form);
      toast.success(`Subscription ${company.sub_id ? 'updated' : 'assigned'} for ${company.name}`);
      onSave();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{company.sub_id ? 'Change Plan' : 'Assign Subscription'} — {company.name}</h3>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="sa-form-group">
              <label className="sa-form-label">Plan *</label>
              <select className="sa-input sa-select" required value={form.plan_id} onChange={e => setForm({...form, plan_id: parseInt(e.target.value)})}>
                <option value="">Select plan...</option>
                {plans.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Billing Cycle</label>
              <select className="sa-input sa-select" value={form.billing_cycle} onChange={e => setForm({...form, billing_cycle: e.target.value})}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Trial Days</label>
              <input className="sa-input" type="number" min="0" max="90" value={form.trial_days} onChange={e => setForm({...form, trial_days: parseInt(e.target.value) || 0})} />
              <small style={{ fontSize: 11, color: '#64748b' }}>Days of free trial before billing</small>
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Notes</label>
              <textarea className="sa-input" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================================================
   COMPANIES PAGE
   ============================================================ */
export default function SuperAdminCompanies() {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignCompany, setAssignCompany] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        subscriptionsAPI.companies({ search, status: statusFilter || undefined }),
        subscriptionsAPI.plans(),
      ]);
      setCompanies(c);
      setPlans(p);
    } catch { /* */ }
  }, [search, statusFilter]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => load(), 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter]);

  const handleCancel = async (subId) => {
    if (!window.confirm('Cancel this subscription?')) return;
    try {
      await subscriptionsAPI.cancel(subId, { reason: 'Cancelled by admin' });
      toast.success('Subscription cancelled');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleReactivate = async (subId) => {
    try {
      await subscriptionsAPI.reactivate(subId);
      toast.success('Subscription reactivated');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteCompany = async (co) => {
    if (!window.confirm(`Delete company "${co.name}"?\n\nThis will permanently remove:\n- All users\n- All subscriptions\n- All invoices\n\nThis CANNOT be undone!`)) return;
    try {
      await subscriptionsAPI.deleteCompany(co.id);
      toast.success(`Company "${co.name}" deleted`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleMarkPaid = async (subId) => {
    try {
      await subscriptionsAPI.confirm(subId, { payment_confirmed: true });
      toast.success('Payment recorded!');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleSuspend = async (co) => {
    const reason = prompt(`Suspend "${co.name}"?\nEnter reason:`);
    if (reason === null) return;
    try {
      await subscriptionsAPI.suspend(co.sub_id, { reason: reason || 'Suspended by admin' });
      toast.success(`Company "${co.name}" suspended`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleUnsuspend = async (co) => {
    try {
      await subscriptionsAPI.unsuspend(co.sub_id);
      toast.success(`Company "${co.name}" unsuspended`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const filters = [
    { key: '', label: 'All Companies' },
    { key: 'active', label: 'Active' },
    { key: 'trialing', label: 'Trialing' },
    { key: 'pending', label: 'Pending' },
    { key: 'suspended', label: 'Suspended' },
    { key: 'expired', label: 'Expired' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div>
      {/* Toolbar */}
      <div className="sa-toolbar">
        <div className="sa-toolbar-left">
          <div className="sa-search-box" style={{ width: 260 }}>
            <Search size={14} />
            <input placeholder="Search companies..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="sa-filter-tabs">
            {filters.map(f => (
              <button key={f.key} className={`sa-filter-tab ${statusFilter === f.key ? 'active' : ''}`} onClick={() => setStatusFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
          {companies.length} companies
        </div>
      </div>

      {/* Table */}
      <div className="sa-card">
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Billing</th>
                <th>Users</th>
                <th>Invoices/mo</th>
                <th>Expiry</th>
                <th className="sa-text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="sa-empty-row"><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : companies.length === 0 ? (
                <tr className="sa-empty-row"><td colSpan={8}>No companies found</td></tr>
              ) : companies.map(co => {
                const st = STATUS_CONFIG[co.sub_status] || STATUS_CONFIG.expired;
                return (
                  <tr key={co.id}>
                    <td>
                      <div className="sa-company-cell">
                        <strong>{co.name}</strong>
                        <span>{co.email}</span>
                      </div>
                    </td>
                    <td><span style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>{co.plan_name || 'No Plan'}</span></td>
                    <td><span className={`sa-badge-status ${st.className}`}>{st.label}</span></td>
                    <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{co.billing_cycle || '—'}</td>
                    <td style={{ fontSize: 12 }}>{co.active_users || 0}/{co.max_users || '—'}</td>
                    <td style={{ fontSize: 12 }}>{co.invoices_this_month || 0}/{co.max_invoices_month === -1 ? '∞' : co.max_invoices_month || '—'}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(co.current_period_end)}</td>
                    <td className="sa-text-right">
                      <div className="sa-row-actions">
                        <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={async () => {
                          setImpersonatingId(co.id);
                          try {
                            await startImpersonation(co.id);
                            toast.success(`Now viewing ${co.name}`);
                            navigate('/dashboard');
                          } catch (err) { toast.error(err.message); setImpersonatingId(null); }
                        }} title="Login As Company" disabled={impersonatingId === co.id}>
                          <LogIn size={12} /> {impersonatingId === co.id ? '...' : 'Login As'}
                        </button>
                        <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate(`/super-admin/companies/${co.id}`)} title="View Details">
                          <Eye size={12} />
                        </button>
                        <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => setAssignCompany(co)} title={co.sub_id ? 'Change Plan' : 'Assign Plan'}>
                          <CreditCard size={12} />
                        </button>
                        {co.sub_status === 'trialing' && co.sub_id && (
                          <button className="sa-btn sa-btn-success sa-btn-sm" onClick={() => handleMarkPaid(co.sub_id)} title="Mark as Paid">
                            <DollarSign size={12} />
                          </button>
                        )}
                        {(co.sub_status === 'active' || co.sub_status === 'trialing') && co.sub_id && (
                          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => handleSuspend(co)} title="Suspend" style={{ color: '#6366f1', borderColor: '#6366f1' }}>
                            <Pause size={12} />
                          </button>
                        )}
                        {co.sub_status === 'suspended' && co.sub_id && (
                          <button className="sa-btn sa-btn-success sa-btn-sm" onClick={() => handleUnsuspend(co)} title="Unsuspend">
                            <Play size={12} />
                          </button>
                        )}
                        {co.sub_status === 'active' && co.sub_id && (
                          <button className="sa-btn sa-btn-danger sa-btn-sm" onClick={() => handleCancel(co.sub_id)} title="Cancel">
                            <Ban size={12} />
                          </button>
                        )}
                        {(co.sub_status === 'cancelled' || co.sub_status === 'expired') && co.sub_id && (
                          <button className="sa-btn sa-btn-success sa-btn-sm" onClick={() => handleReactivate(co.sub_id)} title="Reactivate">
                            <RefreshCw size={11} />
                          </button>
                        )}
                        <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => handleDeleteCompany(co)} title="Delete Company" style={{ color: '#dc2626' }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Modal */}
      {assignCompany && (
        <AssignModal
          company={assignCompany}
          plans={plans}
          onClose={() => setAssignCompany(null)}
          onSave={() => { setAssignCompany(null); load(); }}
        />
      )}
    </div>
  );
}
